import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PillButton } from '../../components/ui';
import { useApp } from '../../context/AppContext';
import { authHeaders, requestJson } from '../../api/http';
import './SharedStoryReview.css';

type Original = { story_id: string; title: string; source_sha256: string };
type Job = { job_id: string; status: string; hold_reason?: string | null };
const root = `${process.env.REACT_APP_API_URL ?? 'http://localhost:8000'}/admin/catalog-generation`;

export default function CatalogGeneration() {
  const { state } = useApp();
  const [stories, setStories] = useState<Original[]>([]);
  const [voice, setVoice] = useState('');
  const [selected, setSelected] = useState('');
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = async () => {
    setLoading(true); setError('');
    try { const result = await requestJson<{stories: Original[]}>(root, { headers: authHeaders(), cache: 'no-store' }); setStories(result.stories); }
    catch { setError('We could not open the original stories. Please try again.'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);
  const prepare = async () => {
    const story = stories.find(s => s.story_id === selected);
    if (!story || !voice || busy) return;
    setBusy(true); setError('');
    try {
      const key = `catalog-generation:${story.story_id}:${story.source_sha256}:${voice}`;
      const request = sessionStorage.getItem(key) || crypto.randomUUID(); sessionStorage.setItem(key, request);
      const result = await requestJson<Job>(root, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ story_id: story.story_id, source_sha256: story.source_sha256, voice_id: voice, request_id: request, generation_authorized: true, neutral_unresolved: true }) });
      setJob(result);
    } catch { setError('We could not start this narration. Your saved request is safe to retry.'); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    if (!job || busy) return;
    setBusy(true); setError('');
    try {
      if (job.status === 'held') await requestJson(`${root}/${job.job_id}/retry`, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ request_id: crypto.randomUUID() }) });
      setJob(await requestJson<Job>(`${root}/${job.job_id}`, { headers: authHeaders(), cache: 'no-store' }));
    }
    catch { setError('We could not check this narration. Please try again.'); }
    finally { setBusy(false); }
  };
  return <section className="shared-story-review" aria-label="Original narration candidates">
    <Link to="/admin/library">Back to library</Link>
    <h1>Prepare original narration</h1>
    <p>Prepare the existing story text in your selected voice. Personal messages stay separate. This creates audio for review and uses no household bedtime.</p>
    {error && <p role="alert">{error}</p>}
    {loading ? <p role="status">Opening original stories...</p> : stories.length === 0 ? <><p>No originals are available here.</p><PillButton variant="primary" onClick={() => void load()}>Try again</PillButton></> : <>
      <label htmlFor="catalog-original">Story</label><select id="catalog-original" disabled={busy} value={selected} onChange={e => { setSelected(e.target.value); setJob(null); }}><option value="">Choose a story</option>{stories.map(s => <option key={s.story_id} value={s.story_id}>{s.title}</option>)}</select>
      <label htmlFor="catalog-voice">Voice</label><select id="catalog-voice" disabled={busy} value={voice} onChange={e => { setVoice(e.target.value); setJob(null); }}><option value="">Choose a voice</option>{state.voices.filter(v => v.status === 'ready').map(v => <option key={v.voice_id} value={v.voice_id}>{v.voice_name}</option>)}</select>
      <p>Unresolved dialogue uses neutral delivery. New audio is not approved or published by this action.</p>
      {job ? <><p role="status">{job.status === 'ready' ? 'Narration is ready for review.' : job.status === 'held' ? 'Narration needs attention. Its saved audio is retained.' : 'Your narration request is saved.'}</p><PillButton variant="primary" busy={busy} onClick={() => void refresh()}>{job.status === 'held' ? 'Try to resume' : 'Check progress'}</PillButton></> : <PillButton variant="primary" busy={busy} disabled={!selected || !voice} onClick={() => void prepare()}>Prepare narration</PillButton>}
    </>}
  </section>;
}
